import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { PromoteTab } from "@/components/PromoteTab";
import { AnalyticsTab } from "@/components/AnalyticsTab";
import { IntegrationTab } from "@/components/IntegrationTab";
import { LocationsTab } from "@/components/LocationsTab";
import { LocationSwitcher } from "@/components/LocationSwitcher";
import { SalesBannerTab } from "@/components/SalesBannerTab";

const STOCK_OPTIONS = [
  { value: "in_stock", label: "In stock" },
  { value: "low_stock", label: "Low stock" },
  { value: "out_of_stock", label: "Out of stock" },
];

export default function DispensaryDashboard() {
  const { user, authChecked } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edited, setEdited] = useState({});
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== "undefined" && window.location.hash === "#locations"
      ? "locations"
      : "profile"
  );

  useEffect(() => {
    if (authChecked && !user) navigate("/dispensary/login");
  }, [authChecked, user, navigate]);

  const refresh = () => {
    setLoading(true);
    Promise.all([api.get("/dispensary/me"), api.get("/dispensary/products")])
      .then(([d, p]) => {
        setData(d.data);
        setProducts(p.data.products);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const saveDispensary = async (patch) => {
    try {
      await api.put("/dispensary/me", patch);
      toast.success("Saved");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveProduct = async (id) => {
    const patch = edited[id];
    if (!patch) return;
    try {
      await api.put(`/dispensary/products/${id}`, patch);
      toast.success("Product updated");
      setEdited((e) => {
        const n = { ...e };
        delete n[id];
        return n;
      });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!authChecked || loading || !data) {
    return (
      <main className="container-narrow py-10">
        <Skeleton className="h-12 w-64 mb-4" />
        <Skeleton className="h-48 rounded-2xl" />
      </main>
    );
  }

  const disp = data.dispensary;
  const planLabel =
    disp.subscription_status === "active"
      ? `Active · ${disp.plan || ""}`
      : disp.subscription_status === "trial"
        ? "Trial active"
        : "Inactive";

  return (
    <main className="container-narrow py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div className="min-w-0 flex-1">
          <div className="eyebrow mb-1 flex items-center gap-2">
            Dashboard
            <LocationSwitcher
              activeName={disp.name}
              onSwitched={refresh}
              onManage={() => {
                // jump to the locations tab — set hash for deep-link
                window.location.hash = "#locations";
                setActiveTab("locations");
              }}
            />
          </div>
          <h1 className="font-serif text-4xl truncate">{disp.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {disp.city}, {disp.state} · {data.product_count} products
          </p>
        </div>
        <div className="flex items-center gap-2">
          {disp.featured && (
            <span className="pill pill-primary text-[10px]">
              <Sparkles className="w-3 h-3" /> Featured
            </span>
          )}
          <span
            className={`pill text-[11px] ${
              disp.subscription_status === "active"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
            }`}
          >
            {planLabel}
          </span>
          <Link
            to="/pricing"
            data-testid="dash-pricing-link"
            className="h-9 px-4 inline-flex items-center rounded-full bg-foreground text-background text-xs hover:bg-foreground/90"
          >
            Manage plan
          </Link>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
        <TabsList className="bg-secondary h-auto p-1 flex-wrap justify-start gap-1">
          <TabsTrigger value="profile" data-testid="dash-tab-profile">
            Profile
          </TabsTrigger>
          <TabsTrigger value="menu" data-testid="dash-tab-menu">
            Menu ({products.length})
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="dash-tab-locations">
            Locations
          </TabsTrigger>
          <TabsTrigger value="promote" data-testid="dash-tab-promote">
            Promote
          </TabsTrigger>
          <TabsTrigger value="sales" data-testid="dash-tab-sales">
            Sales
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="dash-tab-analytics">
            Analytics
          </TabsTrigger>
          <TabsTrigger value="integration" data-testid="dash-tab-integration">
            Integration
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="dash-tab-billing">
            Billing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <ProfileForm disp={disp} onSave={saveDispensary} />
        </TabsContent>

        <TabsContent value="menu" className="mt-6">
          {products.length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-16 text-center text-muted-foreground">
              No products yet. Live ingestion connects once your Dutchie POS is
              wired.
            </div>
          ) : (
            <div className="border border-border rounded-2xl bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Product</th>
                    <th className="px-4 py-3 font-medium">Category</th>
                    <th className="px-4 py-3 font-medium">Price ($)</th>
                    <th className="px-4 py-3 font-medium">THC %</th>
                    <th className="px-4 py-3 font-medium">Stock</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const e = edited[p.id] || {};
                    return (
                      <tr
                        key={p.id}
                        className="border-t border-border"
                        data-testid={`menu-row-${p.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-serif text-base">
                            {p.strain_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.brand} · {p.unit}
                          </div>
                        </td>
                        <td className="px-4 py-3 capitalize">{p.category}</td>
                        <td className="px-4 py-3">
                          <Input
                            data-testid={`menu-price-${p.id}`}
                            type="number"
                            step="0.01"
                            defaultValue={p.price}
                            onChange={(ev) =>
                              setEdited((s) => ({
                                ...s,
                                [p.id]: {
                                  ...s[p.id],
                                  price: parseFloat(ev.target.value),
                                },
                              }))
                            }
                            className="h-9 w-24"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            data-testid={`menu-thc-${p.id}`}
                            type="number"
                            step="0.1"
                            defaultValue={p.thc_pct}
                            onChange={(ev) =>
                              setEdited((s) => ({
                                ...s,
                                [p.id]: {
                                  ...s[p.id],
                                  thc_pct: parseFloat(ev.target.value),
                                },
                              }))
                            }
                            className="h-9 w-20"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            defaultValue={p.stock_status}
                            onValueChange={(v) =>
                              setEdited((s) => ({
                                ...s,
                                [p.id]: { ...s[p.id], stock_status: v },
                              }))
                            }
                          >
                            <SelectTrigger
                              data-testid={`menu-stock-${p.id}`}
                              className="h-9 w-36"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STOCK_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            data-testid={`menu-save-${p.id}`}
                            disabled={!edited[p.id]}
                            onClick={() => saveProduct(p.id)}
                            size="sm"
                            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-9"
                          >
                            Save
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="promote" className="mt-6">
          <PromoteTab products={products} onRefresh={refresh} />
        </TabsContent>

        <TabsContent value="sales" className="mt-6">
          <SalesBannerTab />
        </TabsContent>

        <TabsContent value="locations" className="mt-6">
          <LocationsTab onSwitched={refresh} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AnalyticsTab />
        </TabsContent>

        <TabsContent value="integration" className="mt-6">
          <IntegrationTab />
        </TabsContent>

        <TabsContent value="billing" className="mt-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="border border-border bg-card rounded-2xl p-6">
              <div className="eyebrow mb-2">Current plan</div>
              <div className="font-serif text-3xl">
                {disp.plan
                  ? disp.plan === "annual"
                    ? "Annual"
                    : "Monthly"
                  : "Trial"}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Status:{" "}
                <span className="font-medium text-foreground">
                  {disp.subscription_status}
                </span>
              </p>
              {disp.trial_ends_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Trial ends: {new Date(disp.trial_ends_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="border border-border bg-card rounded-2xl p-6">
              <div className="eyebrow mb-2">Featured placement</div>
              <div className="font-serif text-3xl">
                {disp.featured ? "Active" : "Not active"}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Boost visibility on home & search.
              </p>
              <Link
                to="/pricing"
                data-testid="dash-billing-upgrade"
                className="mt-4 inline-flex h-10 px-5 items-center rounded-full bg-foreground text-background text-xs hover:bg-foreground/90"
              >
                Manage on pricing page
              </Link>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}

const ProfileForm = ({ disp, onSave }) => {
  const [form, setForm] = useState({
    name: disp.name,
    phone: disp.phone || "",
    hours: disp.hours || "",
    address: disp.address || "",
    pickup: disp.pickup,
    delivery: disp.delivery,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="grid sm:grid-cols-2 gap-5"
    >
      <FormField label="Business name">
        <Input
          data-testid="profile-name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="h-11"
        />
      </FormField>
      <FormField label="Phone">
        <Input
          data-testid="profile-phone"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          className="h-11"
        />
      </FormField>
      <FormField label="Hours">
        <Input
          data-testid="profile-hours"
          value={form.hours}
          onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
          className="h-11"
        />
      </FormField>
      <FormField label="Address">
        <Input
          data-testid="profile-address"
          value={form.address}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          className="h-11"
        />
      </FormField>
      <div className="sm:col-span-2 flex gap-4 items-center">
        <label className="flex items-center gap-2 text-sm">
          <input
            data-testid="profile-pickup"
            type="checkbox"
            checked={form.pickup}
            onChange={(e) =>
              setForm((f) => ({ ...f, pickup: e.target.checked }))
            }
          />
          Pickup available
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            data-testid="profile-delivery"
            type="checkbox"
            checked={form.delivery}
            onChange={(e) =>
              setForm((f) => ({ ...f, delivery: e.target.checked }))
            }
          />
          Delivery available
        </label>
      </div>
      <div className="sm:col-span-2">
        <Button
          type="submit"
          data-testid="profile-save"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6"
        >
          Save changes
        </Button>
      </div>
    </form>
  );
};

const FormField = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
      {label}
    </div>
    {children}
  </div>
);
